import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class IBUDataCategory extends BaseObj {
    public ID: number;
    public __id: number;

    public CategoryId: number;
    public __categoryid: number;

    public EventID: number;
    public __eventid: number;

    public IBUID: string;
    public __ibuid: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: { ID?: number; CategoryId?: number; EntityState?: EntityState; EventID?: number; IBUID?: string | null }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<IBUDataCategory>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('IBUDataCategory tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryId = values.CategoryId ?? 0;
        this.__categoryid = values.CategoryId ?? 0;
        Object.defineProperty(this, 'CategoryId', {
            get(): number {
                return this.__categoryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('CategoryId', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IBUID = values.IBUID ?? '';
        this.__ibuid = values.IBUID ?? '';
        Object.defineProperty(this, 'IBUID', {
            get(): string {
                return this.__ibuid;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('IBUID', this.__ibuid, value);
                this.__ibuid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CategoryId": ${JSON.stringify(this.CategoryId)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"IBUID": ${JSON.stringify(this.IBUID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of IBUDataCategory from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CategoryId?: number;
        EventID?: number;
        IBUID?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): IBUDataCategory {
        return new IBUDataCategory(values);
    }

    public static _FromServerJson(json: any): IBUDataCategory {
        return new IBUDataCategory({
            ID: json.ID,
            CategoryId: json.CategoryId,
            EventID: json.EventID,
            IBUID: json.IBUID ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of IBUDataCategory from another instance of IBUDataCategory.
     * @param other The IBUDataCategory you want to clone
     * @returns A cloned IBUDataCategory
     */
    public static FromOther(other: IBUDataCategory): IBUDataCategory {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return IBUDataCategory._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return IBUDataCategory.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'CategoryId' | 'EventID' | 'IBUID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'IBUDataCategory';
}
